/**
 * Preview mode, view-transition reorder support, header/nav reorder drag.
 * Verbatim slice of the iframe bridge script (optimization Phase 10c) —
 * returned text is embedded inside the bridge IIFE; it is NOT module code.
 * The slice is interpolation-free: keep it that way (head/tail interpolations
 * live in iframe-bridge.js).
 */

export function reorderPreviewLayer() {
  return String.raw`  // --- Preview mode ---
  var _previewMode = false;

  function _findCodeStitchNavRoot(toggleEl) {
    return (
      toggleEl.closest('[data-pb-section]') ||
      // Canonical-id exports mark the nav structurally (Workstream A5b);
      // ids stop matching cs-navigation* after the scheme flip.
      toggleEl.closest('[data-pb-nav-root]') ||
      toggleEl.closest('[id^="cs-navigation"]') ||
      toggleEl.closest('header') ||
      toggleEl.closest('nav') ||
      null
    );
  }

  function _findCodeStitchMenu(toggleEl, navRoot) {
    var controlsId = toggleEl.getAttribute('aria-controls');
    if (controlsId) {
      var controlled = document.getElementById(controlsId);
      if (controlled) return controlled;
    }
    if (navRoot) {
      return navRoot.querySelector('[id^="cs-expanded"]') || navRoot.querySelector('.cs-ul');
    }
    return document.querySelector('[id^="cs-expanded"]') || document.querySelector('.cs-ul');
  }

  function _toggleCodeStitchNav(toggleEl) {
    var navRoot = _findCodeStitchNavRoot(toggleEl);
    var menuEl = _findCodeStitchMenu(toggleEl, navRoot);
    if (!menuEl) return false;

    var nextExpanded = menuEl.getAttribute('aria-expanded') !== 'true';
    var navEl = toggleEl.closest('.cs-nav');

    if (navRoot) navRoot.classList.toggle('cs-active', nextExpanded);
    if (navEl) navEl.classList.toggle('cs-active', nextExpanded);
    toggleEl.classList.toggle('cs-active', nextExpanded);
    toggleEl.setAttribute('aria-expanded', String(nextExpanded));
    menuEl.setAttribute('aria-expanded', String(nextExpanded));
    if (menuEl.hasAttribute('aria-hidden')) {
      menuEl.setAttribute('aria-hidden', String(!nextExpanded));
    }
    document.body.classList.toggle('cs-open', nextExpanded);
    return true;
  }

  // CodeStitch nav dropdowns open on '.cs-dropdown.cs-active' — the source
  // site's own script does this and is never crawled, so preview mode has to
  // supply it or an accordion like Mosaic Suites' "Stays" is inert on mobile.
  // Mirrors preview/preview-bundler.js.
  function _toggleCodeStitchDropdown(dropdownEl) {
    dropdownEl.classList.toggle('cs-active');
    var trigger = dropdownEl.querySelector('[aria-expanded]');
    if (trigger) {
      trigger.setAttribute('aria-expanded', String(dropdownEl.classList.contains('cs-active')));
    }
    return true;
  }

  function _toggleStarterNav(toggleEl) {
    var navMenu = document.querySelector('#navbar-menu');
    var nextOpen = true;
    if (navMenu) {
      nextOpen = !navMenu.classList.contains('open');
      navMenu.classList.toggle('open', nextOpen);
    } else {
      nextOpen = !toggleEl.classList.contains('clicked');
    }
    toggleEl.classList.toggle('clicked', nextOpen);
    toggleEl.setAttribute('aria-expanded', String(nextOpen));
    document.body.classList.toggle('open', nextOpen);
    return true;
  }

  function _setDarkTogglePressed(isDark) {
    var toggles = document.querySelectorAll('[id^="dark-mode-toggle"]');
    for (var i = 0; i < toggles.length; i++) {
      toggles[i].setAttribute('aria-pressed', String(isDark));
    }
  }

  function _toggleDarkMode(originEvent) {
    if (typeof window.pbToggleDarkMode === 'function') {
      window.pbToggleDarkMode({ transitionOrigin: originEvent });
    } else {
      var isDark = !document.body.classList.contains('dark-mode');
      document.body.classList.toggle('dark-mode', isDark);
      _setDarkTogglePressed(isDark);
      // The fallback has to persist too, or a canvas rendered without the
      // site-effects bundle loses the choice on the next page switch — the
      // boot script in assembleDocument reads this exact key.
      try {
        if (window.localStorage) window.localStorage.setItem('theme', isDark ? 'dark' : 'light');
      } catch (err) {
        // Storage can be unavailable in sandboxed or private contexts.
      }
    }
  }

  function _injectHeaderZoneMarkers() {
    try {
      var headers = document.querySelectorAll('header');
      for (var h = 0; h < headers.length; h++) {
        var header = headers[h];
        _maybeMarkAsReorderZone(header, 'header-main');

        var children = header.children || [];
        for (var i = 0; i < children.length; i++) {
          var child = children[i];
          var tag = child.tagName;
          if (tag === 'DIV') _maybeMarkAsReorderZone(child, 'header-main');
          if (tag === 'UL' && /top-bar|topbar/i.test(child.className || '')) {
            _maybeMarkAsReorderZone(child, 'header-top');
          }
        }

        var topBars = header.querySelectorAll(':scope > .cs-top-bar, :scope > .cs-top, :scope > [class*="top-bar"]:not(ul)');
        for (var t = 0; t < topBars.length; t++) {
          var inners = topBars[t].children || [];
          for (var j = 0; j < inners.length; j++) {
            if (inners[j].tagName === 'DIV') _maybeMarkAsReorderZone(inners[j], 'header-top');
          }
        }
      }
    } catch (err) { console.warn('[pb-bridge] header reorder-zone scan failed:', err); }
  }

  function _maybeMarkAsReorderZone(el, value) {
    if (!el || !el.getAttribute || el.getAttribute('data-pb-reorder-zone')) return;
    var candidates = 0;
    var children = el.children || [];
    for (var i = 0; i < children.length; i++) {
      var child = children[i];
      var tag = child.tagName;
      if (tag === 'NAV' || tag === 'A' || tag === 'BUTTON' || tag === 'LI') {
        candidates += 1;
        continue;
      }
      var cls = child.className || '';
      if (typeof cls === 'string' && /logo|button|toggle|contact|social|-group|cs-item/i.test(cls)) {
        candidates += 1;
      }
    }
    if (candidates < 2) return;

    // Keep this runtime heuristic identical to element-insert.js's parser-side
    // _looksLikeHeaderFlexContainer(). Canonical HTML parsing cannot know
    // computed display, so a live-only display gate would let optimistic and
    // canonical moves disagree.
    el.setAttribute('data-pb-reorder-zone', value);
  }

  // ===== Phase 20: View Transition support for reorder commits =====
  // Names are tracked explicitly in an array (not via attribute-substring
  // matching) so cleanup never touches inline styles we did not set.
  var _vtNamedElements = [];

  // Kind-segregated prefixes prevent collisions when candidates of different
  // kinds would resolve the same effective eid. Label-driven config: pick
  // exactly one selector + prefix per commit label.
  // Phase 1: DERIVED from the injected reorder specs (historyLabel → {selector:
  // vtSelector, prefix: vtPrefix}) instead of a hardcoded literal, so every new
  // draggable kind self-registers its View Transition config.
  var VT_CONFIG_BY_LABEL = (function() {
    var cfg = {};
    for (var vi = 0; vi < __PB_REORDER_SPECS__.length; vi++) {
      var vspec = __PB_REORDER_SPECS__[vi];
      if (vspec && vspec.historyLabel && vspec.vtPrefix && vspec.vtSelector) {
        cfg[vspec.historyLabel] = { selector: vspec.vtSelector, prefix: vspec.vtPrefix };
      }
    }
    return cfg;
  })();

  function _safeVtName(prefix, eid) {
    if (typeof eid !== 'string') return null;
    // CSS ident: ASCII letter start, then letters/digits/underscore/dash.
    if (!/^[A-Za-z][A-Za-z0-9_-]*$/.test(eid)) return null;
    return prefix + eid;
  }

  function _assignViewTransitionNames(scope, label) {
    if (!scope || !scope.querySelectorAll) return;
    var cfg = VT_CONFIG_BY_LABEL[label];
    if (!cfg) return;
    var candidates = scope.querySelectorAll(cfg.selector);
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      var name = _safeVtName(cfg.prefix, _eidForDragSource(el));
      if (!name) continue;
      el.style.viewTransitionName = name;
      _vtNamedElements.push(el);
    }
  }

  function _clearViewTransitionNames() {
    for (var i = 0; i < _vtNamedElements.length; i++) {
      var el = _vtNamedElements[i];
      if (el && el.style) el.style.viewTransitionName = '';
    }
    _vtNamedElements.length = 0;
  }

  function _runWithViewTransition(scope, label, mutate) {
    // Feature-unavailable OR non-reorder label: run mutation directly.
    if (!document.startViewTransition || !label || !VT_CONFIG_BY_LABEL[label]) {
      mutate();
      return;
    }
    // Belt-and-suspenders: previous run may have failed before cleanup.
    _clearViewTransitionNames();
    // PRE: name OLD elements so the browser snapshots them with stable IDs.
    _assignViewTransitionNames(scope, label);
    var transition = document.startViewTransition(function() {
      mutate();
      // INSIDE callback: innerHTML replacement destroyed the old elements;
      // re-name the NEW ones so the browser can match them for morphing.
      _clearViewTransitionNames();
      _assignViewTransitionNames(scope, label);
    });
    transition.finished.then(function() {
      _clearViewTransitionNames();
      markPositionsDirty();
      markElementRectDirty();
      markHoverTagDirty();
    }, function() {
      _clearViewTransitionNames();
      markPositionsDirty();
      markElementRectDirty();
      markHoverTagDirty();
    });
  }
  // ===== end Phase 20 helpers =====

  function _hamburgerIsVisibleInZone(handle) {
    var zone = handle && handle.parentElement;
    if (!zone) return false;
    var toggle = zone.querySelector('.cs-toggle, .hamburger-menu, [class*="hamburger"]');
    if (!toggle) return false;
    try {
      var style = getComputedStyle(toggle);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
      if (toggle.offsetParent === null) return false;
      return true;
    } catch (err) {
      return false;
    }
  }

  // --- Header/nav reorder drag (desktop horizontal) ---
  var _reorderState = null;
  var _recentlyDragged = 0;
  var _reorderCleaningUp = false;

  function _matchesReorderSelector(el, selector) {
    if (!el || !el.matches || !selector) return false;
    try {
      return el.matches(selector);
    } catch (err) {
      return false;
    }
  }

  function _reorderSpecForSource(el) {
    if (!el || !__PB_REORDER_SPECS__.length) return null;
    for (var i = 0; i < __PB_REORDER_SPECS__.length; i++) {
      var spec = __PB_REORDER_SPECS__[i];
      if (!_matchesReorderSelector(el, spec.sourceSelector)) continue;
      // Region scope must be part of resolution, not a post-filter: nav and
      // footer share the 'ul.cs-ul > li.cs-li' selector, so a footer li matches
      // BOTH specs. Skipping a spec whose required ancestor is absent lets the
      // NEXT spec (correct region) win on the same element.
      if (spec.requiredAncestorSelector && (!el.closest || !el.closest(spec.requiredAncestorSelector))) continue;
      return spec;
    }
    return null;
  }

  function _matchesReorderSibling(el, spec) {
    var selector = spec && (spec.siblingSelector || spec.sourceSelector);
    return _matchesReorderSelector(el, selector);
  }

  function _findReorderHandle(target) {
    if (activeEditable) return null;
    if (_previewMode) return null;
    var el = target && target.nodeType === 1 ? target : null;
    while (el && el !== document.body) {
      // _reorderSpecForSource already enforces requiredAncestorSelector (region
      // scope), so a returned spec is guaranteed valid for this element; nav vs
      // footer (shared selector) resolve to the correct kind by region here.
      var spec = _reorderSpecForSource(el);
      if (spec) {
        // Generic pointer-target guard (spec-driven): reject the match when the
        // ORIGINAL pointerdown target sits inside this selector. Replaces the
        // former hardcoded dropdown/nav-list guards. For nav-link this is
        // 'ul.cs-drop-ul' (Phase 23: dropdown sub-items must not hijack the
        // parent dropdown as a drag source); for header-element it is
        // 'ul.cs-ul, ul.cs-drop-ul' (nav lists belong to the nav-link adapter).
        if (spec.rejectIfTargetClosest && target.closest && target.closest(spec.rejectIfTargetClosest)) {
          return null;
        }
        // Explicit header-only exception (NOT expressible as a selector): when
        // the mobile hamburger is visible the header row isn't in its
        // draggable desktop layout, so the flex-zone children aren't sources.
        if (spec.kind === 'header-element' && _hamburgerIsVisibleInZone(el)) {
          return null;
        }
        return { handle: el, spec: spec };
      }
      el = el.parentElement;
    }
    return null;
  }

  function _eidForDragSource(li) {
    if (!li || !li.getAttribute) return null;
    var own = li.getAttribute('data-pb-eid');
    if (own) return own;
    var child = li.querySelector ? li.querySelector('[data-pb-eid]') : null;
    return child ? child.getAttribute('data-pb-eid') : null;
  }

  function _getInstanceId(el) {
    var wrapper = el && el.closest ? el.closest('[data-pb-instance]') : null;
    return wrapper ? wrapper.getAttribute('data-pb-instance') : null;
  }

  // Axis-aware parent gate. Horizontal ('x') reorder needs a row-like parent
  // (reject flex-column and block flow); vertical ('y') reorder needs a
  // column-like parent (flex-column or block flow). 'grid' accepts any laid-out
  // parent (2D hit-testing decides targets); it has no sources until the
  // card/gallery phase.
  function _reorderParentAllowsAxis(parent, axis) {
    if (!parent) return false;
    var style = getComputedStyle(parent);
    var direction = style.flexDirection || '';
    var isColumn = direction.indexOf('column') === 0;
    var isBlock = style.display === 'block';
    if (axis === 'y') return isColumn || isBlock;
    if (axis === 'grid') return true;
    // Default 'x': horizontal row.
    if (isColumn) return false;
    if (isBlock) return false;
    return true;
  }

  // Resolve an adapter's declared axis to a concrete 'x'/'y' at drag start.
  // 'flow' adapters (e.g. footer link lists) resolve from the parent's live
  // computed layout: a flex column (or block flow) stacks vertically → 'y'; a
  // flex/inline-flex row (including flex-wrap) runs horizontally → 'x'. This is
  // why the same footer can host a vertical .cs-ul and a row-wrapped .cs-ul-1.
  function _resolveReorderAxis(parent, specAxis) {
    if (specAxis !== 'flow') return specAxis || 'x';
    if (!parent) return 'x';
    var style = getComputedStyle(parent);
    var direction = style.flexDirection || '';
    if (direction.indexOf('column') === 0) return 'y';
    if (style.display === 'block') return 'y';
    return 'x';
  }

  function _isUsableReorderRect(rect) {
    return !!rect && isFinite(rect.left) && isFinite(rect.top) && rect.width > 0 && rect.height > 0;
  }

  function _axisForSiblingRects(axis, rects) {
    if (axis !== 'x' || !rects || rects.length < 2) return axis;
    var firstCenterY = rects[0].top + rects[0].height / 2;
    for (var i = 1; i < rects.length; i++) {
      var centerY = rects[i].top + rects[i].height / 2;
      if (Math.abs(centerY - firstCenterY) > Math.max(4, Math.min(rects[i].height, rects[0].height) * 0.4)) {
        return 'grid';
      }
    }
    return axis;
  }

  // A grid can only preview a reorder by SLIDING its neighbours if its cells sit
  // in DOM reading order — that is exactly what makes "the item after me takes my
  // slot" true, and it is what the commit produces (a DOM move, then for pinned
  // families a renumber to the new DOM order). A grid that pins DOM children to
  // arbitrary cells would animate the wrong boxes, so it keeps the drop-target
  // outline instead. Judged from the frozen base rects, never from a class name.
  function _gridFollowsDomOrder(baseRects, parent) {
    if (!baseRects || baseRects.length < 2) return true;
    var rtl = false;
    try { rtl = !!parent && getComputedStyle(parent).direction === 'rtl'; } catch (err) {}
    for (var i = 1; i < baseRects.length; i++) {
      var prev = baseRects[i - 1];
      var cur = baseRects[i];
      var rowTolerance = Math.max(4, Math.min(prev.height, cur.height) * 0.4);
      var prevCenterY = prev.top + prev.height / 2;
      var curCenterY = cur.top + cur.height / 2;
      if (curCenterY - prevCenterY > rowTolerance) continue; // wrapped to the next row
      if (prevCenterY - curCenterY > rowTolerance) return false; // jumped back up
      // Same row: reading order has to advance along the inline axis.
      if (rtl ? cur.left >= prev.left : cur.left <= prev.left) return false;
    }
    return true;
  }

  function _visualPxToCssPx(value, zoom) {
    var safeZoom = isFinite(zoom) && zoom > 0 ? zoom : 1;
    return value / safeZoom;
  }

  // Which axis decides before/after against a GRID target. Inside the target's
  // own row band the inline axis decides — that is what makes a single-row grid
  // behave like a nav row. Deciding on Y there is arbitrary: on a 500px-tall
  // service card, whether the pointer sits above or below the card's vertical
  // centre says nothing about which way the user is dragging, and it silently
  // costs a whole card of travel — 'before' the card that already follows you is
  // a no-op, so nothing moves until the NEXT card becomes the nearest one. Only
  // when the pointer is clear of the band (a different row) does Y decide.
  function _gridResolvedAxis(rect, clientY) {
    var bottom = rect.bottom != null ? rect.bottom : rect.top + rect.height;
    return clientY >= rect.top && clientY <= bottom ? 'x' : 'y';
  }

  function _dropPositionForRect(rect, clientX, clientY, axis, parent) {
    if (axis === 'grid') axis = _gridResolvedAxis(rect, clientY);
    var centerX = rect.left + rect.width / 2;
    var centerY = rect.top + rect.height / 2;
    if (axis === 'y') return clientY < centerY ? 'before' : 'after';
    var rtl = false;
    try { rtl = !!parent && getComputedStyle(parent).direction === 'rtl'; } catch (err) {}
    return rtl ? (clientX > centerX ? 'before' : 'after') : (clientX < centerX ? 'before' : 'after');
  }

  function _dropEdgeForPosition(position, axis, parent) {
    if (axis === 'y' || axis === 'grid') return position === 'before' ? 'top' : 'bottom';
    var rtl = false;
    try { rtl = !!parent && getComputedStyle(parent).direction === 'rtl'; } catch (err) {}
    if (rtl) return position === 'before' ? 'right' : 'left';
    return position === 'before' ? 'left' : 'right';
  }

  function _scrollOffsetScale() {
    var root = getScrollRoot();
    return window.__PB_SCROLL_ROOT__ && root === window.__PB_SCROLL_ROOT__
      ? getCurrentZoom()
      : 1;
  }

  function _stripReorderGhostAttrs(el) {
    if (!el || !el.removeAttribute) return;
    el.removeAttribute('data-pb-eid');
    el.removeAttribute('data-pb-editable');
    el.removeAttribute('data-pb-hover-label');
    el.removeAttribute('data-pb-selected-target');
    el.removeAttribute('data-pb-selected-label');
    el.removeAttribute('data-pb-selected-for-eid');
    el.removeAttribute('data-pb-label-position-anchor');
    el.removeAttribute('data-pb-pill-flip');
    el.removeAttribute('data-pb-ai-context');
    el.removeAttribute('data-pb-section-changed');
    el.removeAttribute('data-pb-change-label');
    el.removeAttribute('data-pb-ai-context-marker');
    el.removeAttribute('contenteditable');
    _clearSectionChromeState(el);
    var markers = el.querySelectorAll ? el.querySelectorAll('.pb-pill-marker') : [];
    for (var mi = 0; mi < markers.length; mi++) {
      if (markers[mi].parentNode) markers[mi].parentNode.removeChild(markers[mi]);
    }
    var nodes = el.querySelectorAll ? el.querySelectorAll('[data-pb-eid], [data-pb-editable], [data-pb-hover-label], [data-pb-selected-target], [data-pb-selected-label], [data-pb-selected-for-eid], [data-pb-section-hovered], [data-pb-section-selected], [data-pb-section-element-hovered], [data-pb-section-first], [data-pb-section-last], [data-pb-ai-context], [data-pb-section-changed], [data-pb-change-label], [data-pb-ai-context-marker], [contenteditable]') : [];
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].removeAttribute('data-pb-eid');
      nodes[i].removeAttribute('data-pb-editable');
      nodes[i].removeAttribute('data-pb-hover-label');
      nodes[i].removeAttribute('data-pb-selected-target');
      nodes[i].removeAttribute('data-pb-selected-label');
      nodes[i].removeAttribute('data-pb-selected-for-eid');
      nodes[i].removeAttribute('data-pb-label-position-anchor');
      nodes[i].removeAttribute('data-pb-pill-flip');
      nodes[i].removeAttribute('data-pb-ai-context');
      nodes[i].removeAttribute('data-pb-section-changed');
      nodes[i].removeAttribute('data-pb-change-label');
      nodes[i].removeAttribute('data-pb-ai-context-marker');
      nodes[i].removeAttribute('contenteditable');
      _clearSectionChromeState(nodes[i]);
    }
  }

  function _activateReorder(e) {
    if (!_reorderState || _reorderState.activated) return;
    var source = _reorderState.candidate;
    var parent = source && source.parentElement;
    var sourceSpec = _reorderState.sourceSpec;
    // Axis was resolved (incl. 'flow') at pointerdown; fall back defensively.
    var axis = _reorderState.axis || _resolveReorderAxis(parent, (sourceSpec && sourceSpec.axis) || 'x');
    if (!source || !parent || !_reorderParentAllowsAxis(parent, axis)) {
      _resetReorderState();
      return;
    }

    var siblings = [];
    var baseRects = [];
    var parentChildren = parent.children || [];
    for (var childI = 0; childI < parentChildren.length; childI++) {
      var child = parentChildren[childI];
      if (!_matchesReorderSibling(child, sourceSpec)) continue;
      var childRect = child.getBoundingClientRect();
      // display:none / detached siblings report 0x0 at (0,0). Excluding them
      // here keeps them out of hit-testing, no-op math, and shift animation.
      if (!_isUsableReorderRect(childRect)) continue;
      siblings.push(child);
      baseRects.push({
        top: childRect.top,
        left: childRect.left,
        right: childRect.right,
        bottom: childRect.bottom,
        width: childRect.width,
        height: childRect.height
      });
    }
    if (siblings.length < 1 || siblings.indexOf(source) === -1) {
      _resetReorderState();
      return;
    }

    try { source.setPointerCapture(_reorderState.pointerId); } catch (captureErr) {}

    var rect = source.getBoundingClientRect();
    // Capture the scroll position at the SAME moment as baseRects so a mid-drag
    // scroll can be corrected by delta (see _pickReorderTarget). baseRects are
    // viewport-relative; without this they desync the instant the page scrolls.
    var scrollX0 = window.pageXOffset || 0;
    var scrollY0 = window.pageYOffset || 0;
    // A nominal x-axis group may wrap at the current viewport. Promote it to
    // 2D targeting for this drag so second-row items remain reachable.
    axis = _axisForSiblingRects(axis, baseRects);
    var sectionScope = source.closest ? source.closest('[data-pb-section]') : null;
    var ghost = document.createElement('div');
    var ghostScope = document.createElement('div');
    ghost.className = 'pb-reorder-ghost';
    ghostScope.className = 'pb-reorder-ghost-scope';
    if (sectionScope && sectionScope.getAttribute('data-pb-section')) {
      ghostScope.setAttribute('data-pb-section', sectionScope.getAttribute('data-pb-section'));
    }
    // Phase 23 polish: pick the visible "chip" inside <li> sources. A nav <li>
    // typically flex-stretches to the header's full height (~64-80px) while
    // its actual content (the inner <a class="cs-li-link">) is only ~28px.
    // Cloning the chip — not the wrapper — gives the ghost the size of the
    // thing the user grabbed, not the size of the layout container.
    var chipElement = source;
    var chipRect = rect;
    if (source.tagName === 'LI' && source.firstElementChild) {
      var firstChild = source.firstElementChild;
      var firstChildRect = firstChild.getBoundingClientRect();
      if (firstChildRect.height > 0 && firstChildRect.height < rect.height - 1) {
        chipElement = firstChild;
        chipRect = firstChildRect;
      }
    }
    var ghostContent = chipElement.cloneNode(true);
    _stripReorderGhostAttrs(ghostContent);
    ghostContent.removeAttribute('data-pb-reorder-source');
    ghostContent.removeAttribute('data-pb-reorder-shift');
    ghostScope.appendChild(ghostContent);
    ghost.appendChild(ghostScope);
    // Phase 16: pickup spring — bounce-in on activation. Class is on the inner
    // scope so the keyframe targets scope.transform (scale) without colliding
    // with the JS-written outer transform (position + tilt).
    ghostScope.classList.add('pb-reorder-ghost-scope--springing');
    ghostScope.addEventListener('animationend', function _onPickupEnd() {
      ghostScope.classList.remove('pb-reorder-ghost-scope--springing');
      ghostScope.removeEventListener('animationend', _onPickupEnd);
    }, { once: true });
    // Pointer coordinates and DOMRect values are visual pixels under CSS zoom,
    // while lengths assigned to nodes inside the zoomed root are CSS pixels.
    // Convert every assigned length or the browser applies zoom a second time.
    var zoom = getCurrentZoom();
    ghost.style.left = _visualPxToCssPx(e.clientX, zoom) + 'px';
    ghost.style.top = _visualPxToCssPx(e.clientY, zoom) + 'px';
    ghost.style.width = _visualPxToCssPx(chipRect.width, zoom) + 'px';
    ghost.style.height = _visualPxToCssPx(chipRect.height, zoom) + 'px';
    document.body.appendChild(ghost);

    source.setAttribute('data-pb-reorder-source', '');
    // Axis-aware gap + shift magnitude: horizontal uses columnGap + item width,
    // vertical uses rowGap + item height. data-pb-reorder-axis on the parent
    // lets the shift/pull CSS pick translateX vs translateY.
    var parentStyle = getComputedStyle(parent);
    var gapProp = axis === 'y' ? parentStyle.rowGap : parentStyle.columnGap;
    var gap = parseFloat(gapProp || parentStyle.gap || '0');
    if (!isFinite(gap)) gap = 0;
    var shiftPx = _visualPxToCssPx(axis === 'y' ? rect.height : rect.width, zoom) + gap;
    parent.style.setProperty('--pb-reorder-shift', shiftPx + 'px');
    parent.style.setProperty('--pb-reorder-pull', _visualPxToCssPx(4, zoom) + 'px');
    parent.setAttribute('data-pb-reorder-axis', axis);

    _reorderState.activated = true;
    _reorderState.sourceEl = source;
    _reorderState.sourceRect = rect;
    _reorderState.parent = parent;
    _reorderState.siblings = siblings;
    _reorderState.sourceSpec = sourceSpec;
    _reorderState.baseRects = baseRects;
    _reorderState.scrollX0 = scrollX0;
    _reorderState.scrollY0 = scrollY0;
    _reorderState.scrollOffsetScale = _scrollOffsetScale();
    _reorderState.axis = axis;
    _reorderState.gridSlides = axis !== 'grid' || _gridFollowsDomOrder(baseRects, parent);
    _reorderState.shiftDirections = [];
    _reorderState.shiftOffsets = [];
    _reorderState.ghost = ghost;
    _reorderState.shiftPx = shiftPx;
    _reorderState.zoom = zoom;
    _reorderState.currentTargetEl = null;
    _reorderState.currentPosition = null;
    _reorderState.dropAllowed = false;
    _reorderState.crossContainerDrop = null;
    _reorderState.rafId = 0;
    // Phase 17: velocity-driven tilt state. Pre-seeded with activation event
    // so the first pointermove has a non-zero delta to compute velocity from.
    _reorderState.prevClientX = e.clientX;
    _reorderState.prevClientY = e.clientY;
    _reorderState.prevTimestamp = e.timeStamp || performance.now();
    _reorderState.tilt = 0;
    _reorderState.reducedMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

    // Phase 21: toggle will-change on siblings only while drag is active.
    // Promotes each sibling to its own GPU compositing layer for the duration
    // of the drag; cleanup removes it so we do not bloat memory at idle.
    for (var willI = 0; willI < siblings.length; willI++) {
      siblings[willI].style.willChange = 'transform';
    }
    // The user-select suppression only takes effect from here on; the ~5px the
    // pointer travelled before the drag armed has already opened a selection, so
    // drop it or the row stays highlighted for the whole drag. Safe to collapse
    // unconditionally: _findReorderHandle refuses to start a drag while a text
    // editable is active, so this can never discard a caret someone is using.
    try {
      var selection = window.getSelection && window.getSelection();
      if (selection && !selection.isCollapsed && selection.removeAllRanges) {
        selection.removeAllRanges();
      }
    } catch (selectionErr) {
      // Selection APIs can throw in a detached or sandboxed document.
    }
    _setReorderActive(true);

    send('reorder-drag-begin', {
      instanceId: _reorderState.instanceId,
      sourceEid: _reorderState.sourceEid,
      rect: {
        top: rect.top + (window.scrollY || 0) * _reorderState.scrollOffsetScale,
        left: rect.left + (window.scrollX || 0) * _reorderState.scrollOffsetScale,
        width: rect.width,
        height: rect.height
      },
      zoom: getCurrentZoom()
    });
  }

  function _reorderScrollDelta() {
    var scale = _reorderState.scrollOffsetScale || 1;
    return {
      dx: ((window.pageXOffset || 0) - (_reorderState.scrollX0 || 0)) * scale,
      dy: ((window.pageYOffset || 0) - (_reorderState.scrollY0 || 0)) * scale
    };
  }

  // Return a hit-test rect for the sibling at index. We adjust the FROZEN rect
  // by the scroll delta rather than re-reading getBoundingClientRect(): mid-drag
  // the siblings carry live transform shifts, so a fresh read would return
  // transformed (wrong) boxes. Scroll-delta on the untransformed base rect keeps
  // targeting correct through a scroll-during-drag.
  function _reorderRectAt(index, item, delta) {
    var base = _reorderState.baseRects && _reorderState.baseRects[index];
    if (!base) return item.getBoundingClientRect();
    return {
      top: base.top - delta.dy,
      left: base.left - delta.dx,
      right: base.right - delta.dx,
      bottom: (base.bottom != null ? base.bottom : base.top + base.height) - delta.dy,
      width: base.width,
      height: base.height
    };
  }

  // Axis-aware hit-test. axis 'x' compares clientX to horizontal midpoints;
  // axis 'y' compares clientY to vertical midpoints; axis 'grid' targets the
  // nearest sibling center in 2D.
  function _pickReorderTarget(clientX, clientY, axis) {
    if (!_reorderState || !_reorderState.siblings) return null;
    axis = axis || 'x';
    var siblings = _reorderState.siblings;
    var source = _reorderState.sourceEl;
    var delta = _reorderScrollDelta();

    var best = null;
    var bestRect = null;
    var bestDistance = Infinity;
    for (var i = 0; i < siblings.length; i++) {
      var item = siblings[i];
      if (item === source) continue;
      var rect = _reorderRectAt(i, item, delta);
      if (!_isUsableReorderRect(rect)) continue;
      var centerX = rect.left + rect.width / 2;
      var centerY = rect.top + rect.height / 2;
      var dx = clientX - centerX;
      var dy = clientY - centerY;
      var distance = axis === 'x' ? dx * dx : (axis === 'y' ? dy * dy : dx * dx + dy * dy);
      if (distance < bestDistance) {
        best = item;
        bestRect = rect;
        bestDistance = distance;
      }
    }
    if (!best) return null;
    return {
      target: best,
      position: _dropPositionForRect(bestRect, clientX, clientY, axis, best.parentElement),
      rect: bestRect
    };
  }

  function _isNoopReorderPosition(targetEl, position) {
    if (!_reorderState || !targetEl) return true;
    var siblings = _reorderState.siblings || [];
    var sourceIdx = siblings.indexOf(_reorderState.sourceEl);
    var targetIdx = siblings.indexOf(targetEl);
    if (sourceIdx === -1 || targetIdx === -1) return true;
    return (position === 'before' && targetIdx === sourceIdx + 1)
      || (position === 'after' && targetIdx === sourceIdx - 1);
  }

  // The offset that carries the sibling occupying rect into the slot occupied by
  // slotRect. Scroll-invariant: both endpoints move by the same scroll delta, so
  // their difference is unaffected. Grid reorder needs BOTH components — a
  // wrapped row's first item moving one slot back travels to the END of the
  // previous row, which is a move on x AND y — so both are always computed and
  // primary carries the single-axis value the x/y CSS rules consume.
  function _reorderSlotOffset(slotRect, rect, axis, fallbackPx) {
    if (!slotRect || !rect) {
      return { x: fallbackPx, y: fallbackPx, primary: fallbackPx };
    }
    var zoom = _reorderState.zoom;
    var dx = _visualPxToCssPx(slotRect.left - rect.left, zoom);
    var dy = _visualPxToCssPx(slotRect.top - rect.top, zoom);
    return { x: dx, y: dy, primary: axis === 'y' ? dy : dx };
  }

  function _writeReorderOffset(el, offset, isGrid) {
    el.style.setProperty('--pb-reorder-offset', offset.primary + 'px');
    if (isGrid) {
      el.style.setProperty('--pb-reorder-offset-x', offset.x + 'px');
      el.style.setProperty('--pb-reorder-offset-y', offset.y + 'px');
    }
  }

  function _clearReorderOffset(el) {
    el.style.removeProperty('--pb-reorder-offset');
    el.style.removeProperty('--pb-reorder-offset-x');
    el.style.removeProperty('--pb-reorder-offset-y');
  }

  function _applyReorderShifts(targetEl, position) {
    if (!_reorderState || !targetEl) return;
    var siblings = _reorderState.siblings || [];
    var sourceIdx = siblings.indexOf(_reorderState.sourceEl);
    var targetIdx = siblings.indexOf(targetEl);
    var insertIdx = position === 'before' ? targetIdx : targetIdx + 1;
    var shiftDirections = [];
    var shiftOffsets = [];
    var baseRects = _reorderState.baseRects || [];

    for (var i = 0; i < siblings.length; i++) {
      siblings[i].removeAttribute('data-pb-reorder-shift');
      _clearReorderOffset(siblings[i]);
    }

    if (sourceIdx === -1 || targetIdx === -1) return;
    var axis = _reorderState.axis || 'x';
    var isGrid = axis === 'grid';
    if (insertIdx > sourceIdx + 1) {
      for (var fwd = sourceIdx + 1; fwd < insertIdx; fwd++) {
        var fwdOffset = _reorderSlotOffset(
          baseRects[fwd - 1], baseRects[fwd], axis, -_reorderState.shiftPx
        );
        siblings[fwd].setAttribute('data-pb-reorder-shift', 'prev');
        _writeReorderOffset(siblings[fwd], fwdOffset, isGrid);
        shiftDirections[fwd] = 'prev';
        shiftOffsets[fwd] = fwdOffset.primary;
      }
    } else if (insertIdx < sourceIdx) {
      for (var back = insertIdx; back < sourceIdx; back++) {
        var backOffset = _reorderSlotOffset(
          baseRects[back + 1], baseRects[back], axis, _reorderState.shiftPx
        );
        siblings[back].setAttribute('data-pb-reorder-shift', 'next');
        _writeReorderOffset(siblings[back], backOffset, isGrid);
        shiftDirections[back] = 'next';
        shiftOffsets[back] = backOffset.primary;
      }
    }
    _reorderState.shiftDirections = shiftDirections;
    _reorderState.shiftOffsets = shiftOffsets;
  }

  function _clearLocalReorderTargets() {
    if (!_reorderState) return;
    var siblings = _reorderState.siblings || [];
    for (var i = 0; i < siblings.length; i++) {
      siblings[i].removeAttribute('data-pb-reorder-shift');
      siblings[i].removeAttribute('data-pb-reorder-pull');
      siblings[i].removeAttribute('data-pb-reorder-grid-target');
      _clearReorderOffset(siblings[i]);
    }
  }

  function _clearCrossContainerTarget() {
    if (!_reorderState || !_reorderState.crossContainerDrop) return;
    var target = _reorderState.crossContainerDrop.targetEl;
    if (target && target.removeAttribute) {
      target.removeAttribute('data-pb-move-target');
      target.removeAttribute('data-pb-move-position');
      target.removeAttribute('data-pb-move-edge');
    }
    _reorderState.crossContainerDrop = null;
  }

  function _requiredRegionMatches(el, spec) {
    return !spec.requiredAncestorSelector || !!(el && el.closest && el.closest(spec.requiredAncestorSelector));
  }

  function _compatibleCrossContainerTarget(rawTarget, clientX, clientY) {
    if (!_reorderState || !_reorderState.activated || !rawTarget) return null;
    var spec = _reorderState.sourceSpec;
    if (!spec || !spec.kind || !spec.itemSelector) return null;
    var el = rawTarget.nodeType === 1 ? rawTarget : rawTarget.parentElement;
    if (!el || !el.closest) return null;

    // Prefer an existing repeat item. This gives an exact before/after anchor
    // and works across both containers and whole sections.
    var candidate = el;
    while (candidate && candidate !== document.body) {
      if (_matchesReorderSibling(candidate, spec)) {
        var candidateSpec = _reorderSpecForSource(candidate);
        var candidateParent = candidate.parentElement;
        if (
          candidateSpec && candidateSpec.kind === spec.kind &&
          candidateParent && candidateParent !== _reorderState.parent &&
          candidate !== _reorderState.sourceEl &&
          !_reorderState.sourceEl.contains(candidate) &&
          !candidate.contains(_reorderState.sourceEl) &&
          _requiredRegionMatches(candidate, spec)
        ) {
          var candidateRect = candidate.getBoundingClientRect();
          if (_isUsableReorderRect(candidateRect)) {
            var targetAxis = _resolveReorderAxis(candidateParent, spec.axis || 'x');
            var targetRects = [];
            var targetChildren = candidateParent.children || [];
            for (var ti = 0; ti < targetChildren.length; ti++) {
              if (!_matchesReorderSibling(targetChildren[ti], spec)) continue;
              var targetRect = targetChildren[ti].getBoundingClientRect();
              if (_isUsableReorderRect(targetRect)) targetRects.push(targetRect);
            }
            targetAxis = _axisForSiblingRects(targetAxis, targetRects);
            var targetEid = _eidForDragSource(candidate);
            var targetInstanceId = _getInstanceId(candidate);
            if (targetEid && targetInstanceId) {
              var position = _dropPositionForRect(candidateRect, clientX, clientY, targetAxis, candidateParent);
              // The edge marker has to be drawn on the axis the position was
              // actually decided on, or a within-row grid drop paints a top/
              // bottom bar for a left/right insertion.
              var edgeAxis = targetAxis === 'grid'
                ? _gridResolvedAxis(candidateRect, clientY)
                : targetAxis;
              return {
                targetEl: candidate,
                targetInstanceId: targetInstanceId,
                targetEid: targetEid,
                position: position,
                edge: _dropEdgeForPosition(position, edgeAxis, candidateParent),
                targetItemSelector: spec.itemSelector
              };
            }
          }
        }
      }
      candidate = candidate.parentElement;
    }

    // Empty compatible repeat groups have no sibling eid to anchor against.
    // If the container itself is editable/eid'd, use the existing inside
    // primitive so the moved subtree becomes its child.
    var parentSelector = spec.parentSelector || (spec.kind === 'header-element' ? '[data-pb-reorder-zone]' : '');
    if (!parentSelector) return null;
    var container = null;
    try { container = el.closest(parentSelector); } catch (err) { container = null; }
    if (!container || container === _reorderState.parent || !_requiredRegionMatches(container, spec)) return null;
    if (_reorderState.sourceEl.contains(container) || container.contains(_reorderState.sourceEl)) return null;
    var containerEid = container.getAttribute('data-pb-eid');
    var containerInstanceId = _getInstanceId(container);
    if (!containerEid || !containerInstanceId) return null;
    return {
      targetEl: container,
      targetInstanceId: containerInstanceId,
      targetEid: containerEid,
      position: 'inside',
      targetItemSelector: null
    };
  }

  function _crossContainerTargetAt(clientX, clientY) {
    if (!document.elementFromPoint) return null;
    var rawTarget = null;
    try { rawTarget = document.elementFromPoint(clientX, clientY); } catch (err) { return null; }
    return _compatibleCrossContainerTarget(rawTarget, clientX, clientY);
  }

  function _pointerWithinLocalBounds(clientX, clientY) {
    if (!_reorderState || !_reorderState.baseRects || !_reorderState.baseRects.length) return false;
    var delta = _reorderScrollDelta();
    var left = Infinity;
    var top = Infinity;
    var right = -Infinity;
    var bottom = -Infinity;
    for (var i = 0; i < _reorderState.siblings.length; i++) {
      var rect = _reorderRectAt(i, _reorderState.siblings[i], delta);
      if (!_isUsableReorderRect(rect)) continue;
      left = Math.min(left, rect.left);
      top = Math.min(top, rect.top);
      right = Math.max(right, rect.right);
      bottom = Math.max(bottom, rect.bottom != null ? rect.bottom : rect.top + rect.height);
    }
    if (!isFinite(left)) return false;
    var margin = 48;
    return clientX >= left - margin && clientX <= right + margin &&
      clientY >= top - margin && clientY <= bottom + margin;
  }

  function _autoScrollReorder(clientY) {
    if (!_reorderState || !_reorderState.activated || typeof window.scrollBy !== 'function') return false;
    var root = getScrollRoot();
    var viewportTop = 0;
    var viewportBottom = window.innerHeight || document.documentElement.clientHeight || 0;
    if (window.__PB_SCROLL_ROOT__ && root === window.__PB_SCROLL_ROOT__ && root.getBoundingClientRect) {
      var rootRect = root.getBoundingClientRect();
      viewportTop = Math.max(0, rootRect.top);
      viewportBottom = Math.min(viewportBottom, rootRect.bottom);
    }
    var viewportHeight = viewportBottom - viewportTop;
    if (viewportHeight <= 0) return false;
    var edge = Math.min(72, Math.max(40, viewportHeight * 0.12));
    var visualDelta = 0;
    if (clientY < viewportTop + edge) visualDelta = -Math.ceil((viewportTop + edge - clientY) / edge * 18);
    else if (clientY > viewportBottom - edge) visualDelta = Math.ceil((clientY - (viewportBottom - edge)) / edge * 18);
    if (!visualDelta) return false;
    var scrollTop = window.pageYOffset || (root && root.scrollTop) || 0;
    var maxScroll = Math.max(0, ((root && root.scrollHeight) || 0) - ((root && root.clientHeight) || viewportHeight));
    if ((visualDelta < 0 && scrollTop <= 0) || (visualDelta > 0 && scrollTop >= maxScroll)) return false;
    try {
      var before = scrollTop;
      window.scrollBy(0, visualDelta / (_reorderState.scrollOffsetScale || 1));
      var after = window.pageYOffset || (root && root.scrollTop) || 0;
      return Math.abs(after - before) > 0.01;
    } catch (err) {
      return false;
    }
  }

  function _updateReorderHoverAt(clientX, clientY, allowAutoScroll) {
    if (!_reorderState || !_reorderState.activated) return;
    if (_reorderState.ghost) {
      // Phase 17: velocity-driven tilt (smoothed 50/50 blend, clamped ±4°).
      // Outer-only — inner scale keyframe is untouched so it can't be stomped.
      var now = performance.now();
      var dt = Math.max(1, now - _reorderState.prevTimestamp);
      var vx = (clientX - _reorderState.prevClientX) / dt;   // px/ms
      var rawTilt = _reorderState.reducedMotion ? 0 : vx * 3;
      _reorderState.tilt = _reorderState.tilt * 0.5 + rawTilt * 0.5;
      var tilt = Math.max(-4, Math.min(4, _reorderState.tilt));
      _reorderState.prevClientX = clientX;
      _reorderState.prevClientY = clientY;
      _reorderState.prevTimestamp = now;
      _reorderState.ghost.style.left = _visualPxToCssPx(clientX, _reorderState.zoom) + 'px';
      _reorderState.ghost.style.top = _visualPxToCssPx(clientY, _reorderState.zoom) + 'px';
      _reorderState.ghost.style.transform =
        'translate3d(-50%, -50%, 0) rotate(' + tilt.toFixed(2) + 'deg)';
    }

    var crossTarget = _crossContainerTargetAt(clientX, clientY);
    if (crossTarget) {
      _clearLocalReorderTargets();
      _clearCrossContainerTarget();
      _reorderState.crossContainerDrop = crossTarget;
      crossTarget.targetEl.setAttribute('data-pb-move-target', '');
      crossTarget.targetEl.setAttribute('data-pb-move-position', crossTarget.position);
      if (crossTarget.edge) crossTarget.targetEl.setAttribute('data-pb-move-edge', crossTarget.edge);
      _reorderState.currentTargetEl = null;
      _reorderState.currentPosition = null;
      _reorderState.dropAllowed = true;
      if (_reorderState.ghost) _reorderState.ghost.setAttribute('data-pb-cross-container', '');
      return allowAutoScroll !== false && _autoScrollReorder(clientY);
    }

    _clearCrossContainerTarget();
    if (_reorderState.ghost) _reorderState.ghost.removeAttribute('data-pb-cross-container');
    if (!_pointerWithinLocalBounds(clientX, clientY)) {
      _clearLocalReorderTargets();
      _reorderState.currentTargetEl = null;
      _reorderState.currentPosition = null;
      _reorderState.dropAllowed = false;
      return allowAutoScroll !== false && _autoScrollReorder(clientY);
    }

    var picked = _pickReorderTarget(clientX, clientY, _reorderState.axis);
    if (!picked || !picked.target) {
      _reorderState.dropAllowed = false;
      return allowAutoScroll !== false && _autoScrollReorder(clientY);
    }
    _reorderState.currentTargetEl = picked.target;
    _reorderState.currentPosition = picked.position;
    _reorderState.dropAllowed = true;
    if (_reorderState.axis === 'grid' && !_reorderState.gridSlides) {
      // A grid whose cells are pinned out of DOM reading order (cs-itemN /
      // cs-pictureN mosaics) cannot FLIP-shift its neighbours sensibly, so it
      // highlights the drop-target cell instead. The ghost still tracks the
      // cursor; the commit's surgical sync + postMoveNormalize produce the
      // actual reflow (View Transition morphs the renumbered cells).
      _applyGridTargetHighlight(picked.target);
    } else {
      _applyReorderShifts(picked.target, picked.position);
      // Magnetic pull reads a gap midpoint on ONE axis, which has no meaning
      // when the shifting pair straddles a row boundary. The 2D slide already
      // shows the drop slot, so grid mode skips the extra nudge.
      if (_reorderState.axis !== 'grid') {
        _applyMagneticPull(picked.target, picked.position, clientX, clientY);
      }
    }
    return allowAutoScroll !== false && _autoScrollReorder(clientY);
  }

  function _applyGridTargetHighlight(targetEl) {
    if (!_reorderState || !_reorderState.siblings) return;
    var sibs = _reorderState.siblings;
    for (var i = 0; i < sibs.length; i++) {
      if (sibs[i] !== targetEl && sibs[i].hasAttribute('data-pb-reorder-grid-target')) {
        sibs[i].removeAttribute('data-pb-reorder-grid-target');
      }
    }
    if (targetEl && !targetEl.hasAttribute('data-pb-reorder-grid-target')) {
      targetEl.setAttribute('data-pb-reorder-grid-target', '');
    }
  }

  // Phase 18: magnetic pull. When cursor is within 30px of the gap midpoint
  // between the hovered target and its filtered neighbor in the drop direction,
  // stamp [data-pb-reorder-pull] on both so the compound CSS rule shifts them
  // an extra ±4px outward. Uses _reorderState.siblings (filtered) for adjacency
  // so decorative excluded elements never participate.
  function _applyMagneticPull(targetEl, position, clientX, clientY) {
    if (!_reorderState || !_reorderState.siblings) return;
    var sibs = _reorderState.siblings;
    if (_reorderState.reducedMotion) {
      for (var reducedI = 0; reducedI < sibs.length; reducedI++) {
        if (sibs[reducedI].hasAttribute('data-pb-reorder-pull')) {
          sibs[reducedI].removeAttribute('data-pb-reorder-pull');
        }
      }
      return;
    }
    var axis = _reorderState.axis || 'x';
    var targetIdx = sibs.indexOf(targetEl);
    if (targetIdx === -1) return;
    var neighborIdx = position === 'before' ? targetIdx - 1 : targetIdx + 1;
    var neighbor = (neighborIdx >= 0 && neighborIdx < sibs.length) ? sibs[neighborIdx] : null;

    // Gap midpoint on the drag axis (live rects → scroll-correct). For y the
    // "first"/"second" elements are top/bottom; the pull tokens stay
    // left(before-side)/right(after-side) — the CSS maps them per axis.
    var midpoint = null;
    var cursor = axis === 'y' ? clientY : clientX;
    if (neighbor) {
      var firstEl  = position === 'before' ? neighbor : targetEl;
      var secondEl = position === 'before' ? targetEl : neighbor;
      var firstRect = firstEl.getBoundingClientRect();
      var secondRect = secondEl.getBoundingClientRect();
      midpoint = axis === 'y'
        ? (firstRect.bottom + secondRect.top) / 2
        : (firstRect.right + secondRect.left) / 2;
    }

    var pullRadius = 30;
    if (midpoint !== null && Math.abs(cursor - midpoint) < pullRadius) {
      var pullA = position === 'before' ? neighbor : targetEl;
      var pullB = position === 'before' ? targetEl : neighbor;
      // Compound CSS selector requires BOTH attributes; JS-side guard mirrors
      // that for clarity and avoids stamping pull on non-shifted siblings.
      if (pullA && pullA.hasAttribute('data-pb-reorder-shift')) {
        pullA.setAttribute('data-pb-reorder-pull', 'left');
      }
      if (pullB && pullB.hasAttribute('data-pb-reorder-shift')) {
        pullB.setAttribute('data-pb-reorder-pull', 'right');
      }
      // Clear pull from any other siblings (in case the active pair changed).
      for (var i = 0; i < sibs.length; i++) {
        if (sibs[i] !== pullA && sibs[i] !== pullB && sibs[i].hasAttribute('data-pb-reorder-pull')) {
          sibs[i].removeAttribute('data-pb-reorder-pull');
        }
      }
    } else {
      // Cursor is outside the pull radius — clear all pull attributes.
      for (var j = 0; j < sibs.length; j++) {
        if (sibs[j].hasAttribute('data-pb-reorder-pull')) {
          sibs[j].removeAttribute('data-pb-reorder-pull');
        }
      }
    }
  }

  function _scheduleReorderHover(e) {
    if (!_reorderState || !_reorderState.activated) return;
    _reorderState.lastClientX = e.clientX;
    _reorderState.lastClientY = e.clientY;
    if (_reorderState.rafId) return;
    _reorderState.rafId = requestAnimationFrame(function() {
      if (!_reorderState) return;
      _reorderState.rafId = 0;
      var autoScrolled = _updateReorderHoverAt(_reorderState.lastClientX, _reorderState.lastClientY, true);
      // Keep autoscrolling while the pointer is stationary near an edge.
      if (autoScrolled && _reorderState && !_reorderState.rafId) {
        _reorderState.rafId = requestAnimationFrame(function autoScrollFrame() {
          if (!_reorderState) return;
          _reorderState.rafId = 0;
          var continued = _updateReorderHoverAt(_reorderState.lastClientX, _reorderState.lastClientY, true);
          if (continued && _reorderState) {
            _reorderState.rafId = requestAnimationFrame(autoScrollFrame);
          }
        });
      }
    });
  }

  function _cleanupReorderVisuals() {
    if (!_reorderState) return;
    if (_reorderState.rafId) {
      cancelAnimationFrame(_reorderState.rafId);
      _reorderState.rafId = 0;
    }
    if (_reorderState.ghost && _reorderState.ghost.parentNode) {
      _reorderState.ghost.parentNode.removeChild(_reorderState.ghost);
    }
    _clearCrossContainerTarget();
    var siblings = _reorderState.siblings || [];
    for (var i = 0; i < siblings.length; i++) {
      siblings[i].removeAttribute('data-pb-reorder-source');
      siblings[i].removeAttribute('data-pb-reorder-shift');
      // Phase 21 hygiene: clear v3-added state so nothing leaks across drags.
      siblings[i].removeAttribute('data-pb-reorder-pull');
      siblings[i].removeAttribute('data-pb-reorder-grid-target');
      _clearReorderOffset(siblings[i]);
      siblings[i].style.willChange = '';
    }
    if (_reorderState.sourceEl) {
      _reorderState.sourceEl.removeAttribute('data-pb-reorder-source');
      try {
        if (_reorderState.sourceEl.hasPointerCapture && _reorderState.sourceEl.hasPointerCapture(_reorderState.pointerId)) {
          _reorderState.sourceEl.releasePointerCapture(_reorderState.pointerId);
        }
      } catch (releaseErr) {}
    }
    if (_reorderState.parent) {
      _reorderState.parent.style.removeProperty('--pb-reorder-shift');
      _reorderState.parent.style.removeProperty('--pb-reorder-pull');
      _reorderState.parent.removeAttribute('data-pb-reorder-axis');
    }
    _setReorderActive(false);
  }

  function _resetReorderState() {
    if (_reorderState && _reorderState.rafId) {
      cancelAnimationFrame(_reorderState.rafId);
    }
    if (_reorderState && _reorderState.longPressTimer) {
      clearTimeout(_reorderState.longPressTimer);
    }
    // Restore the handle's touch-action if a touch long-press had suppressed it.
    if (_reorderState && _reorderState.candidate && _reorderState.candidate.style) {
      _reorderState.candidate.style.touchAction = '';
    }
    _setReorderActive(false);
    _reorderState = null;
  }

  function _commitReorder() {
    if (!_reorderState) return;
    if (!_reorderState.dropAllowed) {
      _cancelReorder('out-of-bounds');
      return;
    }
    var crossDrop = _reorderState.crossContainerDrop;
    if (crossDrop) {
      var crossPayload = {
        sourceInstanceId: _reorderState.instanceId,
        sourceEid: _reorderState.sourceEid,
        sourceKind: _reorderState.sourceSpec ? (_reorderState.sourceSpec.kind || '') : '',
        sourceItemSelector: _reorderState.sourceSpec ? (_reorderState.sourceSpec.itemSelector || '') : '',
        targetInstanceId: crossDrop.targetInstanceId,
        targetEid: crossDrop.targetEid,
        position: crossDrop.position,
        targetItemSelector: crossDrop.targetItemSelector
      };
      _reorderCleaningUp = true;
      _cleanupReorderVisuals();
      _reorderCleaningUp = false;
      _recentlyDragged = performance.now();
      send('element-move-request', crossPayload);
      _resetReorderState();
      return;
    }
    var targetEl = _reorderState.currentTargetEl;
    var position = _reorderState.currentPosition || 'after';
    var targetEid = _eidForDragSource(targetEl);
    var isNoop = _isNoopReorderPosition(targetEl, position);
    var sourceEl = _reorderState.sourceEl;
    var payload = {
      instanceId: _reorderState.instanceId,
      sourceEid: _reorderState.sourceEid,
      targetEid: targetEid,
      position: position,
      kind: _reorderState.sourceSpec ? (_reorderState.sourceSpec.kind || '') : ''
    };

    // Phase 23: pre-move the source to its new DOM position BEFORE cleanup so
    // the user sees an instant snap on drop. The eventual surgical sync will
    // arrive with the same final layout; the iframe-side View Transition
    // wrap snapshots OLD = NEW, producing a no-op morph. Undo/redo paths
    // do NOT go through _commitReorder, so their surgical sync still triggers
    // a real View Transition because the iframe DOM differs from the synced
    // HTML at that point.
    if (
      !isNoop &&
      sourceEl &&
      targetEl &&
      sourceEl !== targetEl &&
      sourceEl.parentNode &&
      targetEl.parentNode === sourceEl.parentNode
    ) {
      try {
        if (position === 'before') {
          sourceEl.parentNode.insertBefore(sourceEl, targetEl);
        } else {
          sourceEl.parentNode.insertBefore(sourceEl, targetEl.nextSibling);
        }
      } catch (preMoveErr) { /* keep going; surgical sync will correct anything off */ }
    }

    _reorderCleaningUp = true;
    _cleanupReorderVisuals();
    _reorderCleaningUp = false;
    _recentlyDragged = performance.now();

    if (payload.targetEid && payload.targetEid !== payload.sourceEid && !isNoop) {
      send('reorder-commit', payload);
    } else {
      send('reorder-drag-cancel', { reason: 'noop' });
    }
    _resetReorderState();
  }

  function _cancelReorder(reason) {
    if (!_reorderState) return;
    // Phase 19 reentrancy guard: if a snap-back animation is already running,
    // subsequent cancel calls (e.g. blur firing after Escape, lostpointercapture
    // racing pointercancel, parent-watchdog firing late) become no-ops.
    if (_reorderState.snappingBack) return;

    var wasActivated = !!_reorderState.activated;
    if (!wasActivated || !_reorderState.ghost || !_reorderState.sourceEl) {
      // Pre-activation cancel — no ghost to animate; just reset.
      _reorderCleaningUp = true;
      _cleanupReorderVisuals();
      _reorderCleaningUp = false;
      _resetReorderState();
      return;
    }

    // Lock the cancel path. Stop the per-frame hover loop so pointermove
    // can't overwrite the snap-back's transform.
    _reorderState.snappingBack = true;
    _reorderState.snapCancelReason = reason || 'cancel';
    _reorderState.activated = false;
    if (_reorderState.rafId) {
      cancelAnimationFrame(_reorderState.rafId);
      _reorderState.rafId = 0;
    }

    var srcRect = _reorderState.sourceEl.getBoundingClientRect();
    var ghost = _reorderState.ghost;
    ghost.classList.add('pb-reorder-ghost--snapping-back');
    ghost.style.left = _visualPxToCssPx(srcRect.left + srcRect.width / 2, _reorderState.zoom) + 'px';
    ghost.style.top = _visualPxToCssPx(srcRect.top + srcRect.height / 2, _reorderState.zoom) + 'px';
    ghost.style.transform = 'translate3d(-50%, -50%, 0) rotate(0deg)';
    // Reset inner scale by removing the springing class — it settles to the
    // resting scale(1.02) defined in the base CSS rule.
    var scope = ghost.querySelector('.pb-reorder-ghost-scope');
    if (scope) scope.classList.remove('pb-reorder-ghost-scope--springing');

    // Clear sibling shifts AND magnetic-pull attributes immediately so the
    // row settles while the ghost is flying back.
    var sibs = _reorderState.siblings || [];
    for (var i = 0; i < sibs.length; i++) {
      sibs[i].removeAttribute('data-pb-reorder-shift');
      sibs[i].removeAttribute('data-pb-reorder-pull');
      sibs[i].removeAttribute('data-pb-reorder-grid-target');
      _clearReorderOffset(sibs[i]);
    }

    // Single-fire cleanup. transitionend AND the 240ms safety timeout race;
    // whichever wins, the 'cleaned' flag prevents the cleanup from running
    // twice. The 'stateRef' capture guards against running cleanup after
    // state has been reset by another code path.
    var stateRef = _reorderState;
    var cleaned = false;
    var finalize = function() {
      if (cleaned) return;
      cleaned = true;
      ghost.removeEventListener('transitionend', finalize);
      if (_reorderState !== stateRef) return;
      _reorderCleaningUp = true;
      _cleanupReorderVisuals();
      _reorderCleaningUp = false;
      _recentlyDragged = performance.now();
      send('reorder-drag-cancel', { reason: stateRef.snapCancelReason });
      _resetReorderState();
    };
    if (_reorderState.reducedMotion) {
      finalize();
      return;
    }
    ghost.addEventListener('transitionend', finalize);
    setTimeout(finalize, 240);
  }

  document.addEventListener('dragstart', function(e) {
    if (_reorderState) {
      e.preventDefault();
      return;
    }
    if (_findReorderHandle(e.target)) e.preventDefault();
  }, true);

  // Phase 8: touch drags arm on a ~250ms long-press so a quick tap/scroll still
  // works. Until armed, a finger move beyond ~10px is treated as a scroll and
  // releases the candidate. Mouse pointers arm immediately (touchArmed=true).
  var TOUCH_LONG_PRESS_MS = 250;
  var TOUCH_SCROLL_RELEASE_PX2 = 100; // (10px)^2

  function _clearLongPressTimer() {
    if (_reorderState && _reorderState.longPressTimer) {
      clearTimeout(_reorderState.longPressTimer);
      _reorderState.longPressTimer = 0;
    }
  }

  document.addEventListener('pointerdown', function(e) {
    if (e.button !== 0) return;
    var match = _findReorderHandle(e.target);
    if (!match) return;
    var handle = match.handle;
    var sourceSpec = match.spec || _reorderSpecForSource(handle);
    if (!sourceSpec) return;
    var sourceEid = _eidForDragSource(handle);
    if (!sourceEid) return;
    // Resolve the concrete axis now (handles 'flow' from the live parent
    // layout) so the gate and, later, the shift geometry agree on one axis.
    var resolvedAxis = _resolveReorderAxis(handle.parentElement, (sourceSpec && sourceSpec.axis) || 'x');
    if (!_reorderParentAllowsAxis(handle.parentElement, resolvedAxis)) return;
    var isTouch = e.pointerType === 'touch';
    _reorderState = {
      candidate: handle,
      startX: e.clientX,
      startY: e.clientY,
      pointerId: e.pointerId,
      activated: false,
      sourceSpec: sourceSpec,
      sourceEid: sourceEid,
      axis: resolvedAxis,
      instanceId: _getInstanceId(handle),
      isTouch: isTouch,
      touchArmed: !isTouch,
      longPressTimer: 0
    };
    if (isTouch) {
      _reorderState.longPressTimer = setTimeout(function() {
        if (!_reorderState || !_reorderState.isTouch) return;
        _reorderState.touchArmed = true;
        _reorderState.longPressTimer = 0;
        // Capture the pointer + suppress native scroll now that a drag is armed.
        try { _reorderState.candidate.setPointerCapture(_reorderState.pointerId); } catch (captureErr) {}
        if (_reorderState.candidate.style) _reorderState.candidate.style.touchAction = 'none';
      }, TOUCH_LONG_PRESS_MS);
    }
  }, true);

  document.addEventListener('pointermove', function(e) {
    if (!_reorderState || e.pointerId !== _reorderState.pointerId) return;
    // Touch, pre-long-press: a move beyond the scroll threshold means the user
    // is scrolling, not dragging — release so the page scrolls natively.
    if (_reorderState.isTouch && !_reorderState.touchArmed) {
      var mdx = e.clientX - _reorderState.startX;
      var mdy = e.clientY - _reorderState.startY;
      if (mdx * mdx + mdy * mdy > TOUCH_SCROLL_RELEASE_PX2) {
        _clearLongPressTimer();
        _resetReorderState();
      }
      return; // no preventDefault — allow native scroll until armed
    }
    if (!_reorderState.activated) {
      var dx = e.clientX - _reorderState.startX;
      var dy = e.clientY - _reorderState.startY;
      if (dx * dx + dy * dy < 25) return;
      e.preventDefault();
      _activateReorder(e);
      if (!_reorderState) return;
    }
    e.preventDefault();
    _scheduleReorderHover(e);
  }, true);

  document.addEventListener('pointerup', function(e) {
    if (!_reorderState || e.pointerId !== _reorderState.pointerId) return;
    if (_reorderState.activated) {
      e.preventDefault();
      if (_reorderState.rafId) {
        cancelAnimationFrame(_reorderState.rafId);
        _reorderState.rafId = 0;
      }
      _updateReorderHoverAt(e.clientX, e.clientY, false);
      _commitReorder();
    } else {
      _resetReorderState();
    }
  }, true);

  document.addEventListener('pointercancel', function(e) {
    if (!_reorderState || e.pointerId !== _reorderState.pointerId) return;
    _cancelReorder('pointercancel');
  }, true);

  document.addEventListener('lostpointercapture', function(e) {
    if (_reorderCleaningUp) return;
    if (!_reorderState || e.pointerId !== _reorderState.pointerId) return;
    if (_reorderState.activated) _cancelReorder('lostpointercapture');
  }, true);

  window.addEventListener('blur', function() {
    if (_reorderState && _reorderState.activated) _cancelReorder('blur');
  });
`;
}
